import { useEffect, useRef, useState } from "react";
import api from "../../../lib/api";
import { useAuth } from "../../../contexts/AuthContext";
import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import ClientFormFields from "../../ClientFormFields";
import { clearOperationKey, d, dialerError, fmtPhone, notifyDialerChanged, operationKeyFor } from "../../../lib/dialer";

/**
 * "Nuevo cliente" desde la burbuja (H6, Turno 4: EXACTAMENTE el mismo formulario de ClientesPage.js/
 * ClienteDetailPage.js, embebido via <ClientFormFields>, con Verizon/AT&T y Google Places/catálogo de
 * ciudad-estado; nada de campos recortados ni lógica duplicada). Usa el POST /clients EXISTENTE y asocia
 * la ficha a la llamada por ID EXPLÍCITO.
 * Orden: 1) reservar el alta (clave de operación + versión), 2) POST /clients, 3) completar (cliente_id).
 * Nunca inventa nombre ni dirección: solo lo que la persona escribe (o confirmó de la consulta externa).
 */
export default function NewClientDialog({ open, onOpenChange, call, prefill, notify, onDone }) {
  const { user } = useAuth();
  // Compañía forzada por el equipo, igual que en Clientes (ClientesPage.js): el bilingüe
  // de equipo Verizon/AT&T no elige; solo el founder/sin equipo ve el selector.
  const teamCompany = (user?.team_features?.company || "").toLowerCase();
  const isInAttTeam = teamCompany === "att";
  const isInVerizonTeam = teamCompany === "verizon";
  const defaultCompany = isInAttTeam ? "att" : "verizon";
  const [form, setForm] = useState({ name: "", phone: "", email: "", account_number: "", address: "", city: "", state: "", zip: "", incident_city: "", incident_state: "", company: defaultCompany });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [retryMatches, setRetryMatches] = useState(null);
  const savingRef = useRef(false);

  useEffect(() => {
    if (open) { setForm((f) => ({ ...f, phone: call?.phone || "", company: defaultCompany, ...(prefill || {}) })); setErr(""); setRetryMatches(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, call?.id]);

  const finish = (client) => {
    clearOperationKey(call.id); onOpenChange(false); notifyDialerChanged();
    notify.success(`Ficha asociada a la llamada: ${client.name}`);
    if (onDone) onDone(client);
  };

  const complete = async (clientId, key, version) => {
    const r = await d.post(`/calls/${call.id}/client-op/complete`, { operation_key: key, client_id: clientId, expected_version: version });
    return r.data;
  };

  const save = async (skipRetryCheck = false) => {
    if (savingRef.current) return;          // doble clic: un solo guardado en vuelo
    if (!form.name.trim()) { setErr("El nombre es requerido"); return; }
    savingRef.current = true; setSaving(true); setErr("");
    try {
      const key = operationKeyFor(call.id);
      const rv = (await d.post(`/calls/${call.id}/client-op/reserve`, { operation_key: key, expected_version: call.client_version || 0 })).data;
      const version = rv.client_version;
      if (rv.joined && rv.op?.state === "completada") { finish({ id: rv.client_id, name: form.name }); return; }
      if (rv.joined && !skipRetryCheck) {
        // Reintento de un alta que quedó a medias: pudo haberse creado la ficha. Se pregunta, no se adivina.
        try {
          const m = (await d.get("/clients/match", { params: { phone: form.phone } })).data;
          if (m.coincidencias?.length) { setRetryMatches({ list: m.coincidencias, key, version }); return; }
        } catch (e) { /* teléfono inválido: se sigue */ }
      }
      const created = (await api.post("/clients", { ...form, status: "activo" })).data;   // POST /clients EXISTENTE
      await complete(created.id, key, version);
      finish(created);
    } catch (e) {
      setErr(dialerError(e));
    } finally { savingRef.current = false; setSaving(false); }
  };

  // Cerrar sin guardar libera la reserva (si no, otro guardado quedaría bloqueado). Mejor esfuerzo.
  const cancel = () => {
    if (call?.id) d.post(`/calls/${call.id}/client-op/cancel`, { operation_key: operationKeyFor(call.id) }).catch(() => {});
    onOpenChange(false);
  };

  const pickExisting = async (c) => {
    if (savingRef.current) return;
    savingRef.current = true; setSaving(true);
    try { await complete(c.id, retryMatches.key, retryMatches.version); finish(c); }
    catch (e) { setErr(dialerError(e)); } finally { savingRef.current = false; setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border sm:max-w-lg" data-testid="bubble-new-client-dialog">
        <DialogHeader><DialogTitle className="font-[Manrope] font-bold">Nuevo cliente para esta llamada</DialogTitle></DialogHeader>
        <div className="text-xs text-muted-foreground">Llamada {fmtPhone(call?.phone)} · se asocia por ID a esta llamada, no por teléfono.</div>
        {err && <div className="text-xs text-red-300 border border-red-500/40 rounded p-2" data-testid="bubble-new-client-error">{err}</div>}
        {retryMatches && (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs space-y-2" data-testid="bubble-retry-matches">
            <div>Un intento anterior de guardar pudo haber creado la ficha. ¿Es una de estas?</div>
            {retryMatches.list.map((c) => (
              <Button key={c.id} size="sm" variant="outline" className="mr-2" disabled={saving} onClick={() => pickExisting(c)} data-testid={`retry-use-${c.id}`}>Usar “{c.name}”</Button>
            ))}
            <Button size="sm" disabled={saving} onClick={() => { setRetryMatches(null); save(true); }} data-testid="retry-create-anyway">Crear de todos modos</Button>
          </div>
        )}
        <ClientFormFields
          form={form}
          setForm={setForm}
          open={open}
          testIdPrefix="bubble-client"
          isGerente={false}
          isInAttTeam={isInAttTeam}
          isInVerizonTeam={isInVerizonTeam}
        />
        <DialogFooter>
          <Button variant="outline" disabled={saving} onClick={() => cancel()} data-testid="bubble-client-cancel">Cancelar</Button>
          <Button disabled={saving} onClick={() => save(false)} data-testid="bubble-client-save">{saving ? "Guardando…" : "Guardar y asociar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
